import { ZoneId } from './store'

export type PropType =
  | 'bone_pile'
  | 'skull'
  | 'sarcophagus'
  | 'torch'
  | 'rubble'
  | 'chain'
  | 'cage'
  | 'jar'
  | 'bookshelf'
  | 'cat_bed'
  | 'yarn_ball'
  | 'food_bowl'
  | 'rug'
  | 'altar'
  | 'banner'
  | 'throne'
  | 'spike_pile'
  | 'goblin_totem'
  | 'blood_stain'
  | 'broken_chair'
  | 'mirror_shard'
  | 'laundry'
  | 'hearth'
  | 'herb_bundle'
  | 'coin_pile'
  | 'shop_counter'
  | 'crate'
  | 'rune_circle'
  | 'dust_cloud'

export type PropDef = {
  type: PropType
  x: number // offset from zone center
  z: number
  breakable?: boolean
  dropsGold?: number
  dropsHp?: number // heals player on break
  variant?: number
}

export type CreatureType = 'rat' | 'bat' | 'moth' | 'beetle'

export type ZoneDef = {
  id: ZoneId
  name: string
  subtitle: string
  theme: 'cold' | 'warm' | 'sinister' | 'cozy' | 'final'
  x: number
  z: number
  size: number
  exits: { to: ZoneId; x: number; z: number }[]
  spawnPoint: { x: number; z: number }
  lightColor: string
  accentColor: string
  floorColor: string
  kind: 'narrative' | 'combat' | 'shop' | 'boss'
  enemyCount?: number
  enemyTypes?: ('grunt' | 'brute' | 'assassin' | 'boss')[]
  props: PropDef[]
  creatures: { type: CreatureType; count: number }[]
  // Central feature drawn behind everything
  centerpiece?:
    | 'throne'
    | 'altar'
    | 'campfire'
    | 'cat_throne'
    | 'shop_counter'
    | 'sarcophagus'
    | null
}

// Helper to scatter props in a rough ring/cluster
const scatter = (
  count: number,
  type: PropType,
  radius: number,
  opts: Partial<PropDef> = {},
): PropDef[] => {
  return Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5
    const r = radius * (0.7 + Math.random() * 0.5)
    return {
      type,
      x: Math.cos(angle) * r,
      z: Math.sin(angle) * r,
      variant: Math.floor(Math.random() * 3),
      ...opts,
    }
  })
}

export const ZONES: Record<ZoneId, ZoneDef> = {
  awakening: {
    id: 'awakening',
    name: 'The Awakening',
    subtitle: 'Floor 1 · Chamber 0',
    theme: 'cold',
    x: -60,
    z: 0,
    size: 12,
    exits: [{ to: 'first_breath', x: -48, z: 0 }],
    spawnPoint: { x: -60, z: 0 },
    lightColor: '#1a1230',
    accentColor: '#8a60d0',
    floorColor: '#120a20',
    kind: 'narrative',
    centerpiece: null,
    props: [
      { type: 'broken_chair', x: -3, z: -2 },
      { type: 'mirror_shard', x: 2, z: -3, variant: 0 },
      { type: 'mirror_shard', x: 3, z: 1, variant: 1 },
      { type: 'laundry', x: -2, z: 3 },
      { type: 'rubble', x: 4, z: 4, breakable: true, dropsGold: 1 },
      { type: 'rubble', x: -5, z: 5, breakable: true, dropsGold: 1 },
      { type: 'dust_cloud', x: 0, z: 0 },
      { type: 'rune_circle', x: 0, z: 0 },
    ],
    creatures: [{ type: 'moth', count: 2 }],
  },
  first_breath: {
    id: 'first_breath',
    name: 'Chamber of First Breath',
    subtitle: 'Where Mordecai waits',
    theme: 'cold',
    x: -30,
    z: 0,
    size: 14,
    exits: [
      { to: 'awakening', x: -43, z: 0 },
      { to: 'tutorial_hollow', x: -16, z: 0 },
    ],
    spawnPoint: { x: -36, z: 0 },
    lightColor: '#0f2038',
    accentColor: '#5090c0',
    floorColor: '#081620',
    kind: 'narrative',
    centerpiece: 'sarcophagus',
    props: [
      ...scatter(4, 'sarcophagus', 6),
      { type: 'torch', x: -9, z: -9 },
      { type: 'torch', x: 9, z: -9 },
      { type: 'torch', x: -9, z: 9 },
      { type: 'torch', x: 9, z: 9 },
      { type: 'rune_circle', x: 0, z: 0 },
      { type: 'crate', x: -6, z: 5, breakable: true, dropsGold: 2 },
      { type: 'crate', x: 5, z: -6, breakable: true, dropsGold: 2 },
      { type: 'bone_pile', x: 7, z: 3 },
      { type: 'bone_pile', x: -7, z: -3 },
    ],
    creatures: [{ type: 'moth', count: 3 }],
  },
  tutorial_hollow: {
    id: 'tutorial_hollow',
    name: 'Tutorial Hollow',
    subtitle: 'Something smells like fur',
    theme: 'sinister',
    x: 0,
    z: 0,
    size: 22,
    exits: [
      { to: 'first_breath', x: -13, z: 0 },
      { to: 'donut_discovery', x: 0, z: -13 },
      { to: 'goblin_warrens', x: 13, z: 0 },
    ],
    spawnPoint: { x: -8, z: 0 },
    lightColor: '#3a1a0f',
    accentColor: '#d06030',
    floorColor: '#1a0a05',
    kind: 'combat',
    enemyCount: 4,
    enemyTypes: ['grunt', 'grunt', 'grunt', 'grunt'],
    centerpiece: 'campfire',
    props: [
      { type: 'torch', x: -10, z: -6 },
      { type: 'torch', x: 10, z: 6 },
      { type: 'blood_stain', x: 2, z: -4, variant: 0 },
      { type: 'blood_stain', x: -3, z: 3, variant: 1 },
      { type: 'bone_pile', x: -5, z: -5 },
      { type: 'bone_pile', x: 6, z: 5 },
      { type: 'skull', x: -8, z: 2 },
      { type: 'skull', x: 8, z: -2 },
      { type: 'chain', x: -10, z: 8 },
      { type: 'cage', x: 10, z: -8, breakable: true, dropsGold: 3 },
      { type: 'crate', x: 4, z: 4, breakable: true, dropsGold: 2 },
      { type: 'crate', x: -4, z: -4, breakable: true, dropsGold: 2 },
      { type: 'rubble', x: 7, z: 0, breakable: true, dropsGold: 1 },
    ],
    creatures: [
      { type: 'rat', count: 2 },
      { type: 'beetle', count: 2 },
    ],
  },
  donut_discovery: {
    id: 'donut_discovery',
    name: "Donut's Discovery",
    subtitle: 'A cat attains consciousness',
    theme: 'warm',
    x: 0,
    z: -30,
    size: 12,
    exits: [
      { to: 'tutorial_hollow', x: 0, z: -18 },
      { to: 'merchant_nook', x: 12, z: -30 },
    ],
    spawnPoint: { x: 0, z: -24 },
    lightColor: '#402818',
    accentColor: '#f0c060',
    floorColor: '#221208',
    kind: 'narrative',
    centerpiece: 'cat_throne',
    props: [
      { type: 'rug', x: 0, z: 2 },
      { type: 'cat_bed', x: 3, z: 3 },
      { type: 'food_bowl', x: -2, z: 4 },
      { type: 'yarn_ball', x: 4, z: -2, breakable: true, dropsGold: 1 },
      { type: 'yarn_ball', x: -4, z: -3, breakable: true, dropsGold: 1 },
      { type: 'yarn_ball', x: 2, z: -4, breakable: true, dropsGold: 1 },
      { type: 'torch', x: -9, z: -9 },
      { type: 'torch', x: 9, z: 9 },
      { type: 'bookshelf', x: -7, z: 0 },
      { type: 'jar', x: 7, z: -5, breakable: true, dropsGold: 3 },
    ],
    creatures: [{ type: 'moth', count: 4 }],
  },
  merchant_nook: {
    id: 'merchant_nook',
    name: "Merchant's Nook",
    subtitle: 'Mrs. Wartbottom-McGillicutty, Proprietor',
    theme: 'cozy',
    x: 30,
    z: -30,
    size: 12,
    exits: [
      { to: 'donut_discovery', x: 18, z: -30 },
      { to: 'goblin_warrens', x: 30, z: -18 },
    ],
    spawnPoint: { x: 24, z: -30 },
    lightColor: '#3a2010',
    accentColor: '#ffb040',
    floorColor: '#201005',
    kind: 'shop',
    centerpiece: 'shop_counter',
    props: [
      { type: 'shop_counter', x: 0, z: 3 },
      { type: 'hearth', x: 0, z: -7 },
      { type: 'jar', x: -3, z: 4, breakable: true, dropsGold: 2 },
      { type: 'jar', x: 3, z: 4, breakable: true, dropsGold: 2 },
      { type: 'jar', x: -4, z: 6 },
      { type: 'jar', x: 4, z: 6 },
      { type: 'herb_bundle', x: -6, z: 0 },
      { type: 'herb_bundle', x: 6, z: 0 },
      { type: 'rug', x: 0, z: 0 },
      { type: 'bookshelf', x: -8, z: -3 },
      { type: 'bookshelf', x: 8, z: -3 },
      { type: 'coin_pile', x: 0, z: 5 },
      { type: 'torch', x: -9, z: 9 },
      { type: 'torch', x: 9, z: 9 },
    ],
    creatures: [{ type: 'moth', count: 3 }],
  },
  goblin_warrens: {
    id: 'goblin_warrens',
    name: 'Goblin Warrens',
    subtitle: 'The sponsors are paying attention now',
    theme: 'sinister',
    x: 30,
    z: 0,
    size: 26,
    exits: [
      { to: 'tutorial_hollow', x: 14, z: 0 },
      { to: 'merchant_nook', x: 30, z: -14 },
      { to: 'throne_of_bones', x: 46, z: 0 },
    ],
    spawnPoint: { x: 22, z: 0 },
    lightColor: '#400810',
    accentColor: '#e02020',
    floorColor: '#1a0404',
    kind: 'combat',
    enemyCount: 8,
    enemyTypes: [
      'grunt',
      'grunt',
      'grunt',
      'grunt',
      'grunt',
      'brute',
      'grunt',
      'brute',
    ],
    centerpiece: 'altar',
    props: [
      ...scatter(6, 'bone_pile', 10),
      { type: 'goblin_totem', x: -8, z: -8 },
      { type: 'goblin_totem', x: 8, z: -8 },
      { type: 'goblin_totem', x: -8, z: 8 },
      { type: 'goblin_totem', x: 8, z: 8 },
      { type: 'skull', x: -5, z: 0 },
      { type: 'skull', x: 5, z: 0 },
      { type: 'skull', x: 0, z: -5 },
      { type: 'skull', x: 0, z: 5 },
      { type: 'spike_pile', x: -12, z: 3 },
      { type: 'spike_pile', x: 12, z: -3 },
      { type: 'cage', x: -11, z: -6, breakable: true, dropsGold: 5 },
      { type: 'cage', x: 11, z: 6, breakable: true, dropsGold: 5 },
      { type: 'blood_stain', x: -3, z: 3 },
      { type: 'blood_stain', x: 3, z: -3 },
      { type: 'blood_stain', x: 6, z: 6 },
      { type: 'torch', x: -14, z: -10 },
      { type: 'torch', x: 14, z: -10 },
      { type: 'torch', x: -14, z: 10 },
      { type: 'torch', x: 14, z: 10 },
      { type: 'crate', x: 5, z: -10, breakable: true, dropsGold: 3 },
      { type: 'crate', x: -5, z: 10, breakable: true, dropsGold: 3 },
    ],
    creatures: [
      { type: 'rat', count: 4 },
      { type: 'bat', count: 2 },
    ],
  },
  throne_of_bones: {
    id: 'throne_of_bones',
    name: 'Throne of Bones',
    subtitle: 'The Goblin King awaits',
    theme: 'final',
    x: 60,
    z: 0,
    size: 28,
    exits: [{ to: 'goblin_warrens', x: 42, z: 0 }],
    spawnPoint: { x: 50, z: 0 },
    lightColor: '#30001a',
    accentColor: '#ff3070',
    floorColor: '#150010',
    kind: 'boss',
    enemyTypes: ['boss'],
    centerpiece: 'throne',
    props: [
      { type: 'banner', x: -5, z: 6 },
      { type: 'banner', x: 5, z: 6 },
      ...scatter(8, 'skull', 12),
      ...scatter(6, 'bone_pile', 10),
      { type: 'torch', x: -16, z: -10 },
      { type: 'torch', x: 16, z: -10 },
      { type: 'torch', x: -16, z: 10 },
      { type: 'torch', x: 16, z: 10 },
      { type: 'chain', x: -12, z: -4 },
      { type: 'chain', x: 12, z: 4 },
      { type: 'blood_stain', x: 0, z: 0 },
      { type: 'crate', x: -10, z: 12, breakable: true, dropsGold: 5 },
      { type: 'crate', x: 10, z: -12, breakable: true, dropsGold: 5 },
    ],
    creatures: [{ type: 'bat', count: 4 }],
  },
  stairs_down: {
    id: 'stairs_down',
    name: 'Stairs Down',
    subtitle: 'Floor 2 awaits',
    theme: 'final',
    x: 80,
    z: 0,
    size: 10,
    exits: [],
    spawnPoint: { x: 80, z: 0 },
    lightColor: '#000000',
    accentColor: '#ffffff',
    floorColor: '#000000',
    kind: 'narrative',
    props: [],
    creatures: [],
  },
}

export const SHOP_ITEMS = [
  {
    id: 'goblin_plasma',
    name: 'Goblin Plasma Potion',
    description:
      'Reconstituted goblin plasma, mixed with energy drink syrup. Tastes like pennies and regret. Heals 50 HP.',
    type: 'consumable' as const,
    effect: 'heal' as const,
    value: 50,
    price: 8,
  },
  {
    id: 'brick',
    name: 'Brick of Questionable Origin',
    description:
      'It is a brick. It wants to be thrown. Who are you to deny a brick its purpose?',
    type: 'oddity' as const,
    effect: 'throw' as const,
    value: 25,
    price: 5,
  },
  {
    id: 'duct_tape',
    name: 'Duct Tape of Holding',
    description:
      'Holds anything to anything else. The multiverse runs on duct tape. This is canon.',
    type: 'oddity' as const,
    value: 0,
    price: 12,
  },
  {
    id: 'suspicious_cheese',
    name: 'Suspicious Cheese',
    description:
      'Mrs. Wartbottom insists it is "aged." She will not specify how. Your insurance does not cover this.',
    type: 'consumable' as const,
    effect: 'heal' as const,
    value: 20,
    price: 3,
  },
]
